import { useState, useEffect, useRef } from 'react';
import { useCity } from '../contexts/CityContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import styles from './SettingsPage.module.css';
import { 
  MdBrightness4, 
  MdDarkMode, 
  MdLightMode, 
  MdAdminPanelSettings, 
  MdSave,
  MdNotifications,
  MdLock,
  MdPrivacyTip,
  MdOutlineNotificationsActive,
  MdOutlineNotificationsOff,
  MdPersonOutline,
  MdCardMembership,
  MdEmail
} from 'react-icons/md';
import { updateUserProfile, logoutUser, upgradeToPremium } from '../services/firebase';
import { updateProfile, updateEmail } from 'firebase/auth';
import AdBanner from '../components/ads/AdBanner';

function SettingsPage() {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { currentCity, setShowCitySelector } = useCity();
  const { theme, setTheme } = useTheme();
  const { isLoggedIn, isAdmin, userProfile, currentUser, refreshUserProfile, isPremium } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [editMode, setEditMode] = useState(false);

  const [activeSection, setActiveSection] = useState<'genel' | 'hesap' | 'bildirimler' | 'gizlilik' | 'uyelik'>('genel');
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    routeAlerts: true,
    announcements: true,
    marketing: false
  });
  const [language, setLanguage] = useState('tr');
  const [languageOpen, setLanguageOpen] = useState(false);
  const [langFocusIndex, setLangFocusIndex] = useState<number>(-1);
  const langMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    shareLocation: true,
    shareUsageData: true,
    allowPersonalizedContent: true
  });

  useEffect(() => {
    if (userProfile) {
      setDisplayName(userProfile.displayName || '');
      setPhoneNumber(userProfile.phoneNumber || ''); // Artık TypeScript hatası vermeyecek
      setEmail(userProfile.email || '');
    }
  }, [userProfile]);

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      if (currentUser) {
        await updateProfile(currentUser, {
          displayName: displayName
        });

        if (email !== userProfile?.email) {
          try {
            await updateEmail(currentUser, email);
          } catch (error: any) {
            if (error.code === 'auth/requires-recent-login') {
              setMessage({ type: 'error', text: 'E-posta değiştirmek için yeniden giriş yapmanız gerekiyor.' });
              return;
            } else {
              setMessage({ type: 'error', text: 'E-posta güncellenirken bir hata oluştu.' });
              return;
            }
          }
        }

        await updateUserProfile(currentUser.uid, {
          displayName,
          phoneNumber, // Artık TypeScript hatası vermeyecek
          email
        });

        await refreshUserProfile();
        setMessage({ type: 'success', text: 'Profil başarıyla güncellendi!' });
        setEditMode(false);
      }
    } catch (error) {
      console.error("Profil güncellenirken hata:", error);
      setMessage({ type: 'error', text: 'Profil güncellenirken bir hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/');
    } catch (error) {
      console.error("Çıkış yapılırken hata:", error);
      setMessage({ type: 'error', text: 'Çıkış yapılırken bir hata oluştu.' });
    }
  };

  const handleUpgradeToPremium = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setMessage(null);
    
    try {
      await upgradeToPremium(currentUser.uid);
      await refreshUserProfile();
      setMessage({ type: 'success', text: 'Premium üyeliğiniz aktifleştirildi!' });
    } catch (error) {
      console.error("Premium üyelik aktifleştirilirken hata:", error);
      setMessage({ type: 'error', text: 'Premium üyelik aktifleştirilirken bir hata oluştu.' });
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = (key: string) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };

  const handlePrivacyChange = (key: string) => {
    setPrivacySettings(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
  setLanguageOpen(false);
  };

  // İleride yeni diller eklendiğinde kolay genişleyebilir
  const languages = [
    { code: 'tr', label: 'Türkçe' }
    //{ code: 'en', label: 'English' }//
  ];

  // Dış tıklama ve Escape ile kapatma
  useEffect(() => {
    if (!languageOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLanguageOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (!languageOpen) return;
      if (e.key === 'Escape') {
        setLanguageOpen(false); return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setLangFocusIndex(prev => {
          const next = (prev + 1) % languages.length;
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setLangFocusIndex(prev => {
          const next = (prev - 1 + languages.length) % languages.length;
          return next;
        });
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (langFocusIndex >= 0) {
          handleLanguageChange(languages[langFocusIndex].code);
        }
      }
    };
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [languageOpen, langFocusIndex, languages]);

  // Menü açıldığında mevcut dili focus index yap
  useEffect(() => {
    if (languageOpen) {
      const idx = languages.findIndex(l => l.code === language);
      setLangFocusIndex(idx);
    }
  }, [languageOpen, language, languages]);

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(false);
    alert('Hesap silme işlemi gerçek uygulamada uygulanmalıdır.');
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const handleMove = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('[data-glow]') as HTMLElement | null;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const mx = ((e.clientX - rect.left) / rect.width) * 100;
      const my = ((e.clientY - rect.top) / rect.height) * 100;
      target.style.setProperty('--mx', mx + '%');
      target.style.setProperty('--my', my + '%');
    };
    const handleTouch = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const el = (e.target as HTMLElement).closest('[data-glow]') as HTMLElement | null;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const mx = ((t.clientX - rect.left) / rect.width) * 100;
      const my = ((t.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--mx', mx + '%');
      el.style.setProperty('--my', my + '%');
      el.classList.add(styles.touching);
    };
    const clearTouch = () => {
      root.querySelectorAll('[data-glow].' + styles.touching).forEach(el => el.classList.remove(styles.touching));
    };
    root.addEventListener('mousemove', handleMove);
    root.addEventListener('touchstart', handleTouch, { passive: true });
    root.addEventListener('touchmove', handleTouch, { passive: true });
    root.addEventListener('touchend', clearTouch);
    return () => {
      root.removeEventListener('mousemove', handleMove);
      root.removeEventListener('touchstart', handleTouch);
      root.removeEventListener('touchmove', handleTouch);
      root.removeEventListener('touchend', clearTouch);
    };
  }, []);

  return (
    <div ref={rootRef} className={styles.settingsPage}>
      {!isPremium && <AdBanner className={styles.settingsAdBanner} />}
      
      <div className={styles.glassCard}>
        <h1>Ayarlar</h1>
      </div>
      
      <div className={styles.settingsLayout}>
        <div className={styles.settingsSidebar}>
          <div 
            className={`${styles.sidebarItem} ${activeSection === 'genel' ? styles.activeSidebarItem : ''}`}
            onClick={() => setActiveSection('genel')}
          >
            <MdBrightness4 />
            <span>Genel Ayarlar</span>
          </div>
          
          <div 
            className={`${styles.sidebarItem} ${activeSection === 'hesap' ? styles.activeSidebarItem : ''}`}
            onClick={() => setActiveSection('hesap')}
          >
            <MdPersonOutline />
            <span>Hesap Ayarları</span>
          </div>
          
            <div 
            className={`${styles.sidebarItem} ${styles.disabledSidebarItem} ${activeSection === 'uyelik' ? styles.activeSidebarItem : ''}`}
            onClick={() => {}} // Empty click handler to make it non-interactive
            >
            <MdCardMembership />
            <span>Üyeliğimi Yönet (Yakında)</span>
            <span className={styles.comingSoonBadge}></span>
            </div>
          
          <div 
            className={`${styles.sidebarItem} ${activeSection === 'bildirimler' ? styles.activeSidebarItem : ''}`}
            onClick={() => {}} // Empty click handler to make it non-interactive
          >
            <MdNotifications />
            <span>Bildirim Ayarları (Yakında)</span>
          </div>
          
          <div 
            className={`${styles.sidebarItem} ${activeSection === 'gizlilik' ? styles.activeSidebarItem : ''}`}
            onClick={() => {}} // Empty click handler to make it non-interactive
          >
            <MdPrivacyTip />
            <span>Gizlilik ve Güvenlik (Yakında)</span>
          </div>
        </div>
        
        <div className={styles.settingsContent}>
          {activeSection === 'genel' && (
            <div className={styles.settingsSection}>
              <h2 className={styles.sectionTitle}>Genel Ayarlar</h2>
              
              <div className={`${styles.settingGroup} ${styles.generalLayout}`}>
                <h3 className={styles.settingGroupTitle}>Görünüm Ayarları</h3>
                <div className={styles.settingItem}>
                  <div className={styles.settingText}>
                    <div className={styles.settingLabel}>Tema</div>
                    <div className={styles.settingDescription}>Renkleri özelleştirin</div>
                  </div>
                  <div className={`${styles.settingControl} ${styles.settingControlWide}`}>
                    <div className={styles.themeSelector}>
                      <button 
                        data-glow
                        className={`${styles.themeButton} ${theme === 'light' ? styles.activeTheme : ''}`}
                        onClick={() => handleThemeChange('light')}
                        title="Açık Tema"
                      >
                        <MdLightMode />
                        <span>Açık</span>
                      </button>
                      <button 
                        data-glow
                        className={`${styles.themeButton} ${theme === 'dark' ? styles.activeTheme : ''}`}
                        onClick={() => handleThemeChange('dark')}
                        title="Karanlık Tema"
                      >
                        <MdDarkMode />
                        <span>Koyu</span>
                      </button>
                      <button 
                        data-glow
                        className={`${styles.themeButton} ${theme === 'system' ? styles.activeTheme : ''}`}
                        onClick={() => handleThemeChange('system')}
                        title="Sistem Teması (Cihazınızın temasını kullanır)"
                      >
                        <MdBrightness4 />
                        <span>Otomatik</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className={styles.settingItem}>
                  <div className={styles.settingText}>
                    <div className={styles.settingLabel}>Dil</div>
                    <div className={styles.settingDescription}>Uygulama dilini değiştir</div>
                  </div>
                  <div className={styles.settingControl}>
                    <div 
                      ref={langMenuRef}
                      className={styles.languageDropdown}
                    >
                      <button
                        type="button"
                        className={styles.languageDropdownTrigger}
                        data-glow
                        aria-haspopup="listbox"
                        aria-expanded={languageOpen}
                        onClick={() => setLanguageOpen(o => !o)}
                      >
                        <span className={styles.languageCurrent}>{languages.find(l => l.code === language)?.label || language}</span>
                        <span className={styles.languageChevron} aria-hidden="true" />
                      </button>
                      {languageOpen && (
                        <div
                          className={styles.languageDropdownMenu}
                          role="listbox"
                          aria-activedescendant={langFocusIndex >= 0 ? `lang-opt-${languages[langFocusIndex].code}` : undefined}
                          onMouseLeave={() => setLangFocusIndex(-1)}
                        >
                          {languages.map((l, idx) => {
                            const active = l.code === language;
                            const focused = idx === langFocusIndex;
                            return (
                              <div
                                key={l.code}
                                id={`lang-opt-${l.code}`}
                                role="option"
                                aria-selected={active}
                                data-glow
                                className={
                                  `${styles.languageOption} ${active ? styles.languageOptionActive : ''} ${focused ? styles.languageOptionFocused : ''}`
                                }
                                onClick={() => handleLanguageChange(l.code)}
                                onMouseEnter={() => setLangFocusIndex(idx)}
                                onMouseLeave={() => setLangFocusIndex(-1)}
                              >
                                <span>{l.label}</span>
                                {active && <span className={styles.languageActiveMark}>✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={`${styles.settingGroup} ${styles.generalLayout}`}>
                <h3 className={styles.settingGroupTitle}>Şehir Ayarları</h3>
                <div className={styles.settingItem}>
                  <div className={styles.settingText}>
                    <div className={styles.settingLabel}>Seçili Şehir</div>
                  </div>
                  <div className={styles.settingControl}>
                    <span className={styles.cityName}>{currentCity?.cityName || 'Şehir Seçimi Zorunludur'}</span>
                    <button 
                      data-glow
                      className={styles.changeButton}
                      onClick={() => setShowCitySelector(true)}
                    >
                      Değiştir
                    </button>
                  </div>
                </div>
              </div>
              
              <div className={styles.settingInfo}>
                <p>Görünüm ve şehir ayarlarınız cihazınızda yerel olarak saklanır. Hesabınızla birlikte farklı cihazlara taşınmaz.</p>
              </div>
            </div>
          )}
          
          {activeSection === 'hesap' && (
            <div className={styles.settingsSection}>
              <h2 className={styles.sectionTitle}>Hesap Ayarları</h2>
              
              {isLoggedIn ? (
                <>
                  {message && (
                    <div className={message.type === 'success' ? styles.success : styles.error}>
                      <p>{message.text}</p>
                    </div>
                  )}

                  <div className={styles.loggedInInfo}>
                    <div className={styles.userInfo}>
                      {userProfile?.photoURL ? (
                        <img 
                          src={userProfile.photoURL} 
                          alt={userProfile.displayName || 'Kullanıcı'} // null yerine string değer sağlıyoruz
                          className={styles.userAvatar} 
                        />
                      ) : (
                        <div className={styles.defaultAvatar}>
                          {userProfile?.displayName?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div className={styles.userDetails}>
                        <h3>{userProfile?.displayName}</h3>
                        <p>{userProfile?.email}</p>
                        <div className={styles.userBadges}>
                          {isAdmin && (
                            <span className={styles.adminBadge}>Yönetici</span>
                          )}
                          {userProfile?.isPremium && (
                            <span className={styles.premiumBadge}>Premium</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {!editMode ? (
                      <div className={styles.accountButtons}>
                        <button 
                          data-glow
                          onClick={() => setEditMode(true)} 
                          className={styles.accountButton}
                        >
                          <span>Profili Düzenle</span>
                        </button>
                        
                        {isAdmin && (
                          <Link to="/admin" data-glow className={styles.accountButton}>
                            <MdAdminPanelSettings />
                            <span>Yönetici Paneli</span>
                          </Link>
                        )}
                      </div>
                    ) : (
                      <form onSubmit={handleProfileUpdate} className={styles.profileForm}>
                        <div className={styles.formCard}>
                          <div className={styles.formCardHeader}>
                            <MdPersonOutline className={styles.formCardIcon} />
                            <h3>Kişisel Bilgiler</h3>
                          </div>
                          <div className={styles.formCardBody}>
                            <div className={styles.formGroup}>
                              <label htmlFor="displayName">Ad Soyad</label>
                              <input
                                type="text"
                                id="displayName"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                required
                                className={styles.formInput}
                                placeholder="Ad ve soyadınızı girin"
                              />
                            </div>

                            <div className={styles.formGroup}>
                              <label htmlFor="phoneNumber">Telefon Numarası</label>
                              <input
                                type="tel"
                                id="phoneNumber"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                placeholder="Telefon numaranızı girin (isteğe bağlı)"
                                className={styles.formInput}
                              />
                            </div>
                          </div>
                        </div>

                        <div className={styles.formCard}>
                          <div className={styles.formCardHeader}>
                            <MdEmail className={styles.formCardIcon} />
                            <h3>İletişim Bilgileri</h3>
                          </div>
                          <div className={styles.formCardBody}>
                            <div className={styles.formGroup}>
                              <label htmlFor="email">E-posta Adresi</label>
                              <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className={styles.formInput}
                                placeholder="E-posta adresinizi girin"
                              />
                              <p className={styles.fieldNote}>E-posta değişikliği için yeniden doğrulama gerekebilir.</p>
                            </div>
                          </div>
                        </div>

                        <div className={styles.formActions}>
                          <button 
                            type="submit" 
                            data-glow
                            className={styles.saveButton}
                            disabled={loading}
                          >
                            <MdSave />
                            {loading ? 'Kaydediliyor...' : 'Kaydet'}
                          </button>
                          <button 
                            type="button" 
                            data-glow
                            className={styles.cancelButton}
                            onClick={() => {
                              setEditMode(false);
                              if (userProfile) {
                                setDisplayName(userProfile.displayName || '');
                                setPhoneNumber(userProfile.phoneNumber || ''); // Artık TypeScript hatası vermeyecek
                                setEmail(userProfile.email || '');
                              }
                            }}
                          >
                            İptal
                          </button>
                        </div>
                      </form>
                    )}
                  </div>

                  <div className={styles.accountActions}>
                    <button 
                      data-glow
                      onClick={handleLogout} 
                      className={styles.logoutButton}
                    >
                      Çıkış Yap
                    </button>
                    
                    <button 
                      data-glow
                      onClick={() => setShowDeleteConfirm(true)} 
                      className={styles.dangerButton}
                    >
                      Hesabımı Sil
                    </button>
                  </div>
                </>
              ) : (
                <div className={styles.authButtons}>
                  <p>Hesap özelliklerini kullanmak için giriş yapın veya kayıt olun.</p>
                  <div className={styles.buttonGroup}>
                    <Link to="/login" data-glow className={styles.loginButton}>Giriş Yap</Link>
                    <Link to="/register" data-glow className={styles.registerButton}>Kayıt Ol</Link>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {activeSection === 'uyelik' && (
            <div className={styles.settingsSection}>
              <h2 className={styles.sectionTitle}>Üyeliğimi Yönet</h2>
              
              {!isLoggedIn ? (
                <div className={styles.authButtons}>
                  <p>Üyelik özelliklerini kullanmak için giriş yapın veya kayıt olun.</p>
                  <div className={styles.buttonGroup}>
                    <Link to="/login" className={styles.loginButton}>Giriş Yap</Link>
                    <Link to="/register" className={styles.registerButton}>Kayıt Ol</Link>
                  </div>
                </div>
              ) : (
                <div className={styles.membershipSection}>
                  {isPremium ? (
                    <div className={styles.premiumInfo}>
                      <div className={styles.premiumBadgeLarge}>Premium Üye</div>
                      <p>Premium avantajlarından yararlanıyorsunuz. Teşekkür ederiz!</p>
                      
                      <div className={styles.premiumFeatures}>
                        <div className={styles.premiumFeature}>
                          <div className={styles.featureIcon}>✓</div>
                          <div className={styles.featureText}>Reklamsız Deneyim</div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.premiumCard}>
                        <h4>Premium Özellikleri Keşfedin</h4>
                        <p>Gerçek zamanlı bildirimler, gelişmiş rota planlaması ve daha fazlası için premium'a yükseltin.</p>
                        <button 
                          data-glow
                          onClick={handleUpgradeToPremium} 
                          className={styles.premiumButton}
                          disabled={loading}
                        >
                          {loading ? 'İşleniyor...' : 'Premium\'a Yükselt'}
                        </button>
                      </div>
                      
                      <AdBanner className={styles.membershipAdBanner} />
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          
          {activeSection === 'bildirimler' && (
            <div className={styles.settingsSection}>
              <h2 className={styles.sectionTitle}>Bildirim Ayarları</h2>
              
              {!isLoggedIn ? (
                <div className={styles.loginPrompt}>
                  <p>Bildirim ayarlarını kişiselleştirmek için lütfen giriş yapın.</p>
                  <Link to="/login" className={styles.loginButton}>Giriş Yap</Link>
                </div>
              ) : (
                <>
                  <div className={styles.settingGroup}>
                    <h3 className={styles.settingGroupTitle}>Bildirim Tercihleri</h3>
                    
                    <div className={styles.notificationItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={notifications.email}
                            onChange={() => handleNotificationChange('email')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.notificationInfo}>
                        <h4>E-posta Bildirimleri</h4>
                        <p>Önemli güncellemeler ve duyurular için e-posta bildirimleri alın</p>
                      </div>
                    </div>
                    
                    <div className={styles.notificationItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={notifications.push}
                            onChange={() => handleNotificationChange('push')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.notificationInfo}>
                        <h4>Push Bildirimleri</h4>
                        <p>Anlık bildirimler ve uyarılar için push bildirimleri alın</p>
                      </div>
                    </div>
                    
                    <div className={styles.notificationItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={notifications.routeAlerts}
                            onChange={() => handleNotificationChange('routeAlerts')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.notificationInfo}>
                        <h4>Rota Uyarıları</h4>
                        <p>Sık kullandığınız hatlar ve rotalar için kesinti ve gecikme bildirimleri alın</p>
                      </div>
                    </div>
                    
                    <div className={styles.notificationItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={notifications.announcements}
                            onChange={() => handleNotificationChange('announcements')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.notificationInfo}>
                        <h4>Sistem Duyuruları</h4>
                        <p>Uygulama güncellemeleri ve yeni özelliklerle ilgili duyurular alın</p>
                      </div>
                    </div>
                    
                    <div className={styles.notificationItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={notifications.marketing}
                            onChange={() => handleNotificationChange('marketing')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.notificationInfo}>
                        <h4>Pazarlama İletileri</h4>
                        <p>Promosyonlar, kampanyalar ve özel tekliflerle ilgili bildirimler alın</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.notificationActions}>
                    <button data-glow className={styles.allOnButton} onClick={() => setNotifications({
                      email: true,
                      push: true,
                      routeAlerts: true,
                      announcements: true,
                      marketing: true
                    })}>
                      <MdOutlineNotificationsActive />
                      Tümünü Aç
                    </button>
                    
                    <button data-glow className={styles.allOffButton} onClick={() => setNotifications({
                      email: false,
                      push: false,
                      routeAlerts: false,
                      announcements: false,
                      marketing: false
                    })}>
                      <MdOutlineNotificationsOff />
                      Tümünü Kapat
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          
          {activeSection === 'gizlilik' && (
            <div className={styles.settingsSection}>
              <h2 className={styles.sectionTitle}>Gizlilik ve Güvenlik</h2>
              
              {!isLoggedIn ? (
                <div className={styles.loginPrompt}>
                  <p>Gizlilik ve güvenlik ayarlarınızı yönetmek için lütfen giriş yapın.</p>
                  <Link to="/login" data-glow className={styles.loginButton}>Giriş Yap</Link>
                </div>
              ) : (
                <>
                  <div className={styles.settingGroup}>
                    <h3 className={styles.settingGroupTitle}>Gizlilik Tercihleri</h3>
                    
                    <div className={styles.privacyItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={privacySettings.shareLocation}
                            onChange={() => handlePrivacyChange('shareLocation')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.privacyInfo}>
                        <h4>Konum Paylaşımı</h4>
                        <p>Daha iyi rota önerileri için konumunuzu paylaşın</p>
                      </div>
                    </div>
                    
                    <div className={styles.privacyItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={privacySettings.shareUsageData}
                            onChange={() => handlePrivacyChange('shareUsageData')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.privacyInfo}>
                        <h4>Kullanım Verisi Paylaşımı</h4>
                        <p>Uygulamayı iyileştirmemize yardımcı olmak için anonim kullanım istatistiklerini paylaşın</p>
                      </div>
                    </div>
                    
                    <div className={styles.privacyItem}>
                      <div className={styles.notificationSwitch}>
                        <label className={styles.switchLabel}>
                          <input 
                            type="checkbox" 
                            checked={privacySettings.allowPersonalizedContent}
                            onChange={() => handlePrivacyChange('allowPersonalizedContent')}
                          />
                          <span className={styles.switchSlider}></span>
                        </label>
                      </div>
                      <div className={styles.privacyInfo}>
                        <h4>Kişiselleştirilmiş İçerik</h4>
                        <p>Kullanım alışkanlıklarınıza göre kişiselleştirilmiş içerik ve öneriler alın</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.settingGroup}>
                    <h3 className={styles.settingGroupTitle}>Güvenlik</h3>
                    
                    <div className={styles.securityOption}>
                      <div className={styles.securityIcon}><MdLock /></div>
                      <div className={styles.securityInfo}>
                        <h4>Şifre Değiştir</h4>
                        <p>Hesabınızın güvenliği için düzenli olarak şifrenizi değiştirin</p>
                        <Link to="/forgot-password" data-glow className={styles.securityLink}>
                          Şifremi Değiştir
                        </Link>
                      </div>
                    </div>
                    
                    <div className={styles.settingInfo}>
                      <div className={styles.infoIcon}>i</div>
                      <p>Hesabınızın güvenliği için şifrenizi düzenli olarak değiştirmenizi ve başka platformlarda kullanmadığınız güçlü bir şifre seçmenizi öneririz.</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {!isPremium && <AdBanner className={styles.bottomAdBanner} />}
      
      {showDeleteConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Hesabınızı Silmek İstediğinizden Emin misiniz?</h3>
            <p className={styles.modalText}>
              Bu işlem geri alınamaz. Hesabınız ve tüm verileriniz kalıcı olarak silinecektir.
            </p>
            <div className={styles.modalButtons}>
              <button 
                data-glow
                className={styles.modalCancelButton}
                onClick={() => setShowDeleteConfirm(false)}
              >
                İptal
              </button>
              <button 
                data-glow
                className={styles.modalDeleteButton}
                onClick={handleDeleteAccount}
              >
                Hesabımı Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsPage;
